import { Api } from "@/services/ApiService"
import { SchultenMedia } from "@/services/DataService"
import { Chat, Chats, ChatReadResponse, SendMessageResponse, TypingResponse, Message } from "@/features/chat/chat.types"

// The server's own message, only for a 4xx that carries one (validation, too long).
// Anything else (offline, timeout, 5xx) gives "": the caller shows a generic text,
// never axios' "Network Error" or "timeout of 300000ms exceeded".
const sendFailureMessage = (data: any): string => {
    const status = data?.status
    const serverMessage = data?.response?.data?.message
    return status >= 400 && status < 500 && typeof serverMessage === "string" ? serverMessage : ""
}

class ChatService {
    async getChats(): Promise<{ success: boolean; message: string; data?: Chat[] }> {
        try {
            const dataResult = await Api.get(SchultenMedia.API.chats.getChats)
            const data = dataResult.ok ? dataResult.data : null

            if (!data || data?.error) {
                return {
                    success: false,
                    message: data?.message || "Failed to fetch conversations.",
                }
            }

            const payload: Chat[] = Array.isArray(data?.data)
                ? data.data
                : (Array.isArray(data) ? data : []);

            return {
                success: true,
                message: "Successfully fetched chats",
                data: payload,
            }
        } catch (error) {
            console.error("ChatService Error:", error)
            return {
                success: false,
                message: "An unexpected error occurred while fetching chats",
            }
        }
    }

    async chatRead(conversation_id: number): Promise<{ success: boolean; message: string; data?: ChatReadResponse }> {
        try {
            const data = await Api.post(SchultenMedia.API.chats.chatRead, {
                conversation_id,
            })

            if (!data || data?.error) {
                return {
                    success: false,
                    message: data?.message || "Failed to read chat.",
                }
            }

            return {
                success: true,
                message: "Successfully read chat",
                data: data,
            }
        } catch (error) {
            console.error("ChatService Error:", error)
            return {
                success: false,
                message: "An unexpected error occurred while reading chat",
            }
        }
    }

    async sendVoiceNote(conversation_id: number, file: File): Promise<{ success: boolean; message: string; data?: SendMessageResponse }> {
        try {
            const form = new FormData()
            form.append('conversation_id', String(conversation_id))
            form.append('voice_note', file, file.name)
            const data = await Api.post(SchultenMedia.API.chats.sendMessage, form, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 300 * 1000,
            } as any)
            if (!data || data?.error) {
                return { success: false, message: sendFailureMessage(data) }
            }
            return { success: true, message: "Successfully sent message", data }
        } catch (error) {
            console.error("ChatService Error:", error)
            return { success: false, message: "" }
        }
    }

    async sendMessage(conversation_id: number, message?: string, attachments?: File[] | any[], message_id?: number): Promise<{ success: boolean; message: string; data?: SendMessageResponse }> {
        try {
            const payload: any = {
                conversation_id,
                message,
                attachments: undefined,
            }

            if (message_id) payload.message_id = message_id;

            // If files provided, convert to form-data
            if (attachments && attachments.length && attachments[0] instanceof File) {
                const form = new FormData()
                form.append('conversation_id', String(conversation_id))
                if (message_id) form.append('message_id', String(message_id))
                if (message) form.append('message', message)
                    ; (attachments as File[]).forEach((file, idx) => form.append('attachments[]', file))
                const data = await Api.post(SchultenMedia.API.chats.sendMessage, form, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    timeout: 300 * 1000, // 300 seconds = 5 minutes
                } as any)
                if (!data || data?.error) {
                    return { success: false, message: sendFailureMessage(data) }
                }
                return {
                    success: true,
                    message: "Successfully sent message",
                    data: data,
                }
            }

            const data = await Api.post(SchultenMedia.API.chats.sendMessage, payload)

            if (!data || data?.error) {
                return { success: false, message: sendFailureMessage(data) }
            }

            return {
                success: true,
                message: "Successfully sent message",
                data: data,
            }
        } catch (error) {
            console.error("ChatService Error:", error)
            return { success: false, message: "" }
        }
    }

    async downloadAttachment(uuid: string): Promise<{ success: boolean; message: string; data?: any }> {
        try {
            const dataResult = await Api.get(SchultenMedia.API.chats.downloadAttachment.replace(":uuid", uuid))
            const data = dataResult.ok ? dataResult.data : null

            if (!data || data?.error) {
                return {
                    success: false,
                    message: data?.message || "Failed to download attachment.",
                }
            }

            return {
                success: true,
                message: "Successfully downloaded attachment",
                data: data,
            }
        }
        catch (error) {
            console.error("ChatService Error:", error)
            return {
                success: false,
                message: "An unexpected error occurred while downloading attachment",
            }
        }
    }

    async typing(conversation_id: number, opts?: { signal?: AbortSignal }): Promise<{ success: boolean; message: string; data?: TypingResponse }> {
        try {
            const data = await Api.post(
                SchultenMedia.API.chats.typing,
                { conversation_id },
                opts?.signal ? { signal: opts.signal } as any : {}
            )

            if (!data || data?.error) {
                return {
                    success: false,
                    message: data?.message || "Failed to send typing indicator.",
                }
            }

            return {
                success: true,
                message: "Successfully sent typing indicator",
                data: data,
            }
        }
        catch (error) {
            console.error("ChatService Error:", error)
            return {
                success: false,
                message: "An unexpected error occurred while sending typing indicator",
            }
        }
    }

    async leave(conversation_id: number): Promise<{ success: boolean; message: string; }> {
        try {
            const data = await Api.post(
                SchultenMedia.API.chats.leave,
                { conversation_id },
            )

            if (!data || data?.error) {
                return {
                    success: false,
                    message: data?.message || "Failed to leave the chat",
                }
            }

            return {
                success: true,
                message: "Successfully left the chat",
            }
        }
        catch (error) {
            console.error("ChatService Error:", error)
            return {
                success: false,
                message: "An unexpected error occurred while leaving the chat",
            }
        }
    }

    async getMessages(conversation_uuid: string, page: number): Promise<{ success: boolean; message: string; data?: { data: Message[]; meta?: any; links?: any } }> {
        try {
            const dataResult = await Api.get(
                SchultenMedia.API.chats.getMessages.replace(":uuid", String(conversation_uuid)),
                { params: { page } }
            )
            const data = dataResult.ok ? dataResult.data : null

            if (!data || data?.error) {
                return {
                    success: false,
                    message: data?.message || "Failed to fetch messages.",
                }
            }

            return {
                success: true,
                message: "Successfully fetched messages",
                data: data,
            }
        } catch (error) {
            console.error("ChatService Error:", error)
            return {
                success: false,
                message: "An unexpected error occurred while fetching messages",
            }
        }
    }

    async deleteMessage(conversation_id: number, message_id: number): Promise<{ success: boolean; message: string }> {
        try {
            const data = await Api.delete(SchultenMedia.API.chats.deleteMessage.replace(":id", String(message_id)), { conversation_id })

            if (!data || data?.error) {
                return {
                    success: false,
                    message: data?.message || "Failed to delete the message.",
                }
            }

            return {
                success: true,
                message: "The message successfully deleted",
            }
        } catch (error) {
            console.error("ChatService Error:", error)
            return {
                success: false,
                message: "An unexpected error occurred while deleting the message",
            }
        }
    }
}

export default new ChatService()